import { QueryParams, Service } from '@/services/api';

export interface DocumentsKPI {
  policiesWithNoPolicyDocumentCount: number;
  policiesWithNoBinderCount: number;
  unpublishedPolicyDocumentCount: number;
  unpublishedBinderCount: number;
  unvalidatedBinderCount: number;
}

export interface PoliciesKPI {
  policiesWithNoPremiumOrFeesCount: number;
  policiesWithInvalidDatesCount: number;
  policiesWithPossibleInvalidPolicyNumberCount: number;
  totalPolicyCount: number;
}

export interface ClientReadinessPolicy {
  policyId: number;
  mainPolicyNumber: string;
  description: string;
}

export interface RuleViolatingPolicy {
  policyId: number;
  mainPolicyNumber: string;
  description: string;
  coverageType: string;
  effectiveDate?: string;
  expirationDate?: string;
  policyPremium?: number;
  feesBilled?: number;
  billMode?: string | null;
  noLimitRule: string;
  invalidRetentionRule: boolean;
  invalidAttachmentPointRule: boolean;
  invalidPolicyNumber: boolean;
  invalidMissingPremiumOrFees: boolean;
  invalidDates: boolean;
}

export interface RuleViolatingProgram {
  programId: number;
  programName: string;
  programYear: number;
  programType: string;
  accountId: number;
  accountName: string;
  noExposuresRule: boolean;
  noServicingRolesRule: boolean;
  inactiveServicerRule: boolean;
  inactiveServicers: InactiveServicer[];
}

export interface InactiveServicer {
  name: string;
  servicingRoleTitle: string;
  inactiveReason: string;
}

export interface UnvalidatedBinder {
  policyId: number;
  mainPolicyNumber: string;
  description: string;
  documentName: string;
  promptResultId: number;
}

export interface ClientReadinessQueryParams {
  showNonCAC?: boolean;
  showCurrentOnly?: boolean;
}

export interface PoliciesWithoutDocumentsQueryParams extends ClientReadinessQueryParams {
  documentType?: string;
}

export type PolicyDocumentStatusValue = 'missing' | 'unpublished' | 'published';

export interface PolicyDocumentStatusRow {
  policyId: number;
  mainPolicyNumber: string;
  description: string;
  documentId: string | null;
  documentName: string | null;
  published: boolean | null;
  status: PolicyDocumentStatusValue;
}

export type DocumentType = 'Binder' | 'Policy';

export interface PolicyDocumentStatusQueryParams extends ClientReadinessQueryParams {
  documentType?: DocumentType;
}

class ClientReadinessService extends Service {
  async getDocumentsKPI(accountId: number, queryParams?: ClientReadinessQueryParams): Promise<DocumentsKPI> {
    return this.get<DocumentsKPI>(`/v2/client-readiness/documents-kpi/${accountId}`, queryParams as QueryParams).then(this.handleResponse);
  }

  async getPoliciesKPI(accountId: number, queryParams?: ClientReadinessQueryParams): Promise<PoliciesKPI> {
    return this.get<PoliciesKPI>(`/v2/client-readiness/policies-kpi/${accountId}`, queryParams as QueryParams).then(this.handleResponse);
  }

  async getPoliciesWithoutDocuments(accountId: number, queryParams?: PoliciesWithoutDocumentsQueryParams): Promise<ClientReadinessPolicy[]> {
    return this.get<ClientReadinessPolicy[]>(`/v2/client-readiness/policies-without-documents/${accountId}`, queryParams as QueryParams).then(
      this.handleResponse,
    );
  }

  async getPolicyDocumentStatus(accountId: number, queryParams: PolicyDocumentStatusQueryParams): Promise<PolicyDocumentStatusRow[]> {
    return this.get<PolicyDocumentStatusRow[]>(`/v2/client-readiness/policy-documents/${accountId}`, queryParams as QueryParams).then(
      this.handleResponse,
    );
  }

  async getRuleViolatingPolicies(accountId: number, queryParams?: ClientReadinessQueryParams): Promise<RuleViolatingPolicy[]> {
    return this.get<any>(`/v2/client-readiness/rule-violating-policies/${accountId}`, queryParams as QueryParams).then(this.handleResponse);
  }

  async getRuleViolatingPrograms(accountId: number, queryParams?: ClientReadinessQueryParams): Promise<RuleViolatingProgram[]> {
    return this.get<any>(`/v2/client-readiness/rule-violating-programs/${accountId}`, queryParams as QueryParams).then(this.handleResponse);
  }

  async getUnvalidatedBinders(accountId: number, queryParams?: ClientReadinessQueryParams): Promise<UnvalidatedBinder[]> {
    return this.get<any>(`/v2/client-readiness/unvalidated-binders/${accountId}`, queryParams as QueryParams).then(this.handleResponse);
  }
}

export default new ClientReadinessService();
